// ClaimsLogModal.jsx
import React, { useState, useEffect } from "react";
import { Modal, Spin, Select, message, Button } from "antd";
import styled from "@emotion/styled";
import { useForm, Controller } from "react-hook-form";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo } from "redux/selectors";
import queryKeys from "common/queryKey";
import dayjs from "dayjs";

/* ─── Helpers ─── */
const todayISO = () => new Date().toISOString().split("T")[0];

/* ─── Styled Components ─── */
const ModalBody = styled.div`
  * { outline: none !important; }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
    @media (max-width: 576px) { grid-template-columns: 1fr; }
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    .required-star { color: #ef4444; margin-left: 2px; }
  }

  select,
  input[type="text"],
  input[type="date"],
  input[type="number"] {
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    padding: 6px 10px;
    height: 36px;
    font-size: 14px;
    color: #111827;
    background: #fff;
    width: 100%;
    box-sizing: border-box;
    font-family: inherit;
    transition: border-color 0.2s;
    &:focus { border-color: #00c48c; }
    &.field-error { border-color: #ff4d4f; }
  }

  .error {
    color: #ef4444;
    font-size: 12px;
    margin-top: 2px;
  }

  /* ── Ant Select overrides ── */
  .ant-select { width: 100%; }
  .ant-select-selector {
    border: 1px solid #d9d9d9 !important;
    border-radius: 6px !important;
    min-height: 36px !important;
    font-size: 14px !important;
    font-family: inherit !important;
    box-shadow: none !important;
    display: flex !important;
    align-items: center !important;
  }
  .ant-select-focused .ant-select-selector,
  .ant-select-selector:focus-within {
    border-color: #00c48c !important;
    box-shadow: none !important;
  }
  .ant-select-selector.field-error { border-color: #ff4d4f !important; }
  .ant-select-selection-placeholder { font-size: 14px !important; color: #bfbfbf !important; }
  .ant-select-selection-item { font-size: 14px !important; }

  /* ── Amount row ── */
  .rate-row {
    display: flex;
    align-items: center;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    overflow: hidden;
    height: 36px;
    transition: border-color 0.2s;
    &.field-error { border-color: #ff4d4f; }
    &:focus-within { border-color: #00c48c; }
    .currency-symbol {
      padding: 0 10px;
      background: #f3f4f6;
      border-right: 1px solid #d9d9d9;
      font-size: 13px;
      color: #374151;
      height: 100%;
      display: flex;
      align-items: center;
      white-space: nowrap;
      font-weight: 500;
      gap: 6px;
    }
    input {
      border: none !important;
      border-radius: 0 !important;
      height: 100% !important;
      flex: 1;
      padding: 0 10px;
    }
  }

  /* ── Radio group ── */
  .radio-group {
    display: flex;
    align-items: center;
    gap: 16px;
    padding-top: 4px;
    label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      font-weight: 400;
      cursor: pointer;
    }
    input[type="radio"] {
      width: 16px;
      height: 16px;
      accent-color: #00c48c;
      border: none;
      padding: 0;
    }
  }

  /* ── Description toolbar ── */
  .description-toolbar {
    display: flex;
    align-items: center;
    gap: 4px;
    border: 1px solid #d9d9d9;
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    padding: 6px 10px;
    background: #fafafa;
    button {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 13px;
      font-weight: bold;
      color: #555;
      padding: 2px 5px;
      border-radius: 3px;
      &:hover { background: #ebebeb; }
    }
    .separator { width: 1px; height: 16px; background: #d9d9d9; margin: 0 4px; }
  }

  textarea.desc-area {
    border: 1px solid #d9d9d9;
    border-top: none;
    border-radius: 0 0 6px 6px;
    padding: 10px;
    font-size: 14px;
    resize: vertical;
    min-height: 90px;
    font-family: inherit;
    width: 100%;
    box-sizing: border-box;
    height: auto;
    &:focus { border-color: #00c48c; }
  }

  /* ── Bills section ── */
  .bills-section {
    margin-bottom: 16px;
  }

  .bill-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    margin-bottom: 8px;
    font-size: 13px;
    color: #374151;

    .bill-icon { font-size: 16px; flex-shrink: 0; }
    .bill-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .bill-size { color: #9ca3af; font-size: 12px; white-space: nowrap; }
    .remove-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: #ef4444;
      font-size: 16px;
      padding: 0 4px;
      line-height: 1;
      display: flex;
      align-items: center;
      border-radius: 4px;
      &:hover { background: #fee2e2; }
    }
  }

  .upload-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #deefee;
    color: #00685F;
    border: none;
    border-radius: 6px;
    padding: 8px 20px;
    font-size: 14px;
    cursor: pointer;
    font-weight: 500;
    width: fit-content;
    transition: background 0.2s;
    &:hover { background: #deefee; }
  }

  .bills-limit-note {
    font-size: 12px;
    color: #9ca3af;
    margin-top: 4px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  .btn-cancel {
    padding: 8px 24px;
    border: 1px solid #000000;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
    font-size: 14px;
    &:hover { background: #ffffff; }
  }
  .btn-submit {
    padding: 8px 24px;
    border: none;
    border-radius: 6px;
    background: #00685F ;
    cursor: pointer;
  color: #ffffff !important;
    font-size: 14px;
    font-weight: 500;
    &:hover { background: #00685F ; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
`;

/* ─── Sub-components ─── */
const Label = ({ children, required }) => (
  <label>
    {children}
    {required && <span className="required-star">*</span>}
  </label>
);

const DescriptionBlock = ({ value, onChange }) => (
  <>
    <div className="description-toolbar">
      <button type="button"><b>B</b></button>
      <button type="button"><i>I</i></button>
      <button type="button"><u>U</u></button>
      <button type="button"><s>S</s></button>
      <div className="separator" />
      <button type="button">1.</button>
      <button type="button">•</button>
      <div className="separator" />
    </div>
    <textarea
      className="desc-area"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </>
);

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/* ─── Default form values ─── */
const buildDefaultValues = (editRecord, matterId, matterData) => {
  if (editRecord) {
    return {
      clientid: editRecord.clientid || "",
      clientname: editRecord.clientname || editRecord.client || "",
      matterid: editRecord.matterid || "",
      mattername: editRecord.mattername || editRecord.matter || "",
      dateoflog: editRecord.dateoflog || editRecord.dateOfLog || todayISO(),
      taxtype: editRecord.taxtype || editRecord.taxType || "VAT",
      type: editRecord.type || "Travel",
      currency: editRecord.currency || "USD",
      totalamount: editRecord.totalamount != null
        ? String(editRecord.totalamount)
        : String(editRecord.totalAmount ?? "").replace(/[^0-9.]/g, ""),
      description: editRecord.description || "",
    };
  }
  return {
    clientid: matterData?.clientId || "",
    clientname: matterData?.clientName || "",
    matterid: matterId || matterData?.matterId || "",
    mattername: matterData?.jobTitle || "",
    dateoflog: todayISO(),
    taxtype: "VAT",
    type: "Travel",
    currency: "USD",
    totalamount: "",
    description: "",
  };
};

/* ─── Date range filter helper ───
   Returns true if today falls within [start, end] (inclusive).
   If both are null → no restriction → always include. */
const isWithinBookingRange = (item) => {
  const today = dayjs().startOf("day");
  const start = item.actualBookedStartDate
    ? dayjs(item.actualBookedStartDate).startOf("day")
    : null;
  const end = item.actualBookedEndDate
    ? dayjs(item.actualBookedEndDate).startOf("day")
    : null;

  if (!start && !end) return false;
  if (start && !end) return !today.isBefore(start);
  if (!start && end) return !today.isAfter(end);
  return !today.isBefore(start) && !today.isAfter(end);
};

/* ─────────────── ClaimsLogModal ─────────────── */
const ClaimsLogModal = ({
  visible,
  onClose,
  editRecord = null,
  matterId,
  matterData,
  onSuccess,
  claimId,
}) => {
  const isEdit = !!editRecord;
  const userInfo = useSelector((state) => getUserInfo(state));

  // allMatters holds the full unfiltered list for client-based filtering
  const [clientList, setClientList] = useState([]);
  const [allMatters, setAllMatters] = useState([]);
  const [filteredMatters, setFilteredMatters] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);

  // ── Multiple bills state ──
  const [bills, setBills] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);

  /* ── react-hook-form ── */
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: buildDefaultValues(editRecord, matterId, matterData),
  });

  const selectedClientId = watch("clientid");
  const selectedCurrency = watch("currency");
  const taxtype = watch("taxtype");
  const type = watch("type");
  const description = watch("description");

  /* ── Fetch clients + matters from combined API ── */
  useEffect(() => {
    if (!visible) return;

    const fetchLists = async () => {
      setListsLoading(true);
      try {
        const { url } = queryKeys.getClientListForLogs(`?userid=${userInfo?.userid}`);
        const response = await CALL_FEATCH_API(url, "get");

        // Resolve data array — handle both direct and wrapped response shapes
        const resolvedData =
          Array.isArray(response?.data) ? response.data :
            Array.isArray(response?.response?.data) ? response.response.data :
              null;

        if (!resolvedData) {
          console.warn("[ClaimsLog] Could not resolve data array from response.");
          return;
        }

        // Apply date-range filter
        const dateFiltered = resolvedData.filter(isWithinBookingRange);

        console.log("[ClaimsLog] after date filter:", dateFiltered.length, "of", resolvedData.length);

        // Build matters list — support both camelCase and snake_case field names
        const matters = dateFiltered.map((item, index) => ({
          key: item?.matterId ?? item?.matterid ?? index,
          matterid: item?.matterId ?? item?.matterid,
          mattername: item?.matterName ?? item?.mattername,
          clientid: item?.clientId ?? item?.clientid,
          clientname: item?.clientName ?? item?.clientname,
          currency: item?.currency ?? "USD"
        }));

        // Build unique clients list derived from matters
        const clients = Object.values(
          matters.reduce((acc, item) => {
            if (item.clientid && !acc[item.clientid]) {
              acc[item.clientid] = {
                bdo_id: item.clientid,
                clientname: item.clientname,
              };
            }
            return acc;
          }, {})
        );

        setAllMatters(matters);
        setClientList(clients);

        // Pre-filter matters if a client is already selected (edit mode)
        const initClientId = editRecord?.clientid || matterData?.clientId || "";
        if (initClientId) {
          setFilteredMatters(matters.filter((m) => String(m.clientid) === String(initClientId)));
        } else {
          setFilteredMatters(matters);
        }
      } catch (err) {
        console.error("[ClaimsLog] Failed to fetch lists:", err);
        message.error("Failed to load client/matter list.");
      } finally {
        setListsLoading(false);
      }
    };

    fetchLists();
  }, [visible]);

  /* ── Reset form when modal opens ── */
  useEffect(() => {
    if (!visible) return;
    reset(buildDefaultValues(editRecord, matterId, matterData));
    setBills([]);
    const saved = Array.isArray(editRecord?.attachment) ? editRecord.attachment : [];
    setExistingAttachments(saved.filter((a) => a?.objectKey || a?.url || a?.name));
  }, [visible, editRecord, matterId, matterData]);

  /* ── Filter matters when client changes ── */
  useEffect(() => {
    if (!selectedClientId) {
      setFilteredMatters(allMatters);
      return;
    }
    setFilteredMatters(allMatters.filter((m) => String(m.clientid) === String(selectedClientId)));
    if (!isEdit) {
      setValue("matterid", "");
      setValue("mattername", "");
    }
  }, [selectedClientId, allMatters]);

  /* ── Bills handlers ── */
  const handleAddBills = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (!newFiles.length) return;
    setBills((prev) => [...prev, ...newFiles]);
    e.target.value = "";
  };

  const handleRemoveBill = (index) => {
    setBills((prev) => prev.filter((_, i) => i !== index));
  };

  /* ── S3 upload helper ── */
  const uploadBillsToS3 = async (files) => {
    if (!files.length) return [];

    const presignedRes = await CALL_API(
      "insyts/timesheet/claims?file",
      "post",
      { filenames: files.map((f) => f.name) }
    );

    const uploadFileData =
      presignedRes?.response?.uploadFileData ??
      presignedRes?.uploadFileData;

    if (!uploadFileData?.length) {
      throw new Error("Failed to get presigned URLs");
    }

    await Promise.all(
      files.map((file, index) =>
        fetch(uploadFileData[index].presignedUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
          mode: "cors",
          credentials: "omit",
        }).then((res) => {
          if (!res.ok) throw new Error(`S3 upload failed for "${file.name}"`);
        })
      )
    );

    return uploadFileData.map((item, index) => ({
      documentId: item.objectKey.split("_")[1] ?? String(Date.now()),
      objectKey: item.objectKey,
      fileName: files[index].name,
      fileType: files[index].type || "application/octet-stream",
      fileSize: files[index].size,
    }));
  };

  /* ── Submit ── */
  const onSubmit = async (data) => {
    const finalClientName =
      data.clientname ||
      clientList.find((c) => c.bdo_id === data.clientid)?.clientname ||
      "";

    try {
      const newAttachments = await uploadBillsToS3(bills);
      const attachedDocuments = [...existingAttachments, ...newAttachments];

      const basePayload = {
        clientid: data.clientid,
        clientname: finalClientName,
        matterid: data.matterid,
        mattername: data.mattername,
        dateoflog: data.dateoflog,
        taxtype: data.taxtype,
        type: data.type,
        currency: data.currency,
        totalamount: parseFloat(data.totalamount) || 0,
        attachment: attachedDocuments,
        description: data.description,
      };

      if (isEdit) {
        await CALL_API(
          "insyts/timesheet/claims",
          "patch",
          { ...basePayload, claimsid: claimId || editRecord?.claimsid, updatedby: userInfo?.userid || "" }
        );
      } else {
        await CALL_FEATCH_API(
          "insyts/timesheet/claims",
          "post",
          { ...basePayload, createdby: userInfo?.userid || "" }
        );
      }

      message.success(isEdit ? "Claims log updated!" : "Claims log created!");
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("onSubmit error:", err);
      message.error(err?.message || "Something went wrong. Please try again.");
    }
  };

  /* ── Currency symbol helper ── */
  const CURRENCY_SYMBOLS = { USD: "$", EUR: "€", GBP: "£", KES: "KSh", JPY: "¥", INR: "₹", AUD: "A$", CAD: "C$", CHF: "Fr", CNY: "¥", SGD: "S$", AED: "د.إ" };
  const currencySymbol = CURRENCY_SYMBOLS[selectedCurrency] || selectedCurrency;

  return (
    <Modal
      title={isEdit ? "Editing Claims Log" : "Creating Claims Log"}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Spin spinning={listsLoading}>
        <ModalBody>
          <form onSubmit={handleSubmit(onSubmit)}>

            {/* ── Row 1: Client + Matter ── */}
            <div className="form-row">
              <div className="form-group">
                <Label required>Client</Label>
                <Controller
                  name="clientid"
                  control={control}
                  rules={{ required: "Client is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      showSearch
                      placeholder="Select client"
                      value={field.value || undefined}
                      onChange={(selectedId) => {
                        const selected = clientList.find((c) => String(c.bdo_id) === String(selectedId));
                        field.onChange(selectedId);
                        setValue("clientname", selected?.clientname || "");
                      }}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      options={clientList.map((c) => ({
                        value: c.bdo_id,
                        label: c.clientname || c.bdo_id,
                      }))}
                      notFoundContent={listsLoading ? <Spin size="small" /> : "No clients found"}
                      className={errors.clientid ? "field-error" : ""}
                    />
                  )}
                />
                {errors.clientid && <span className="error">{errors.clientid.message}</span>}
              </div>

              <div className="form-group">
                <Label required>Matter</Label>
                <Controller
                  name="matterid"
                  control={control}
                  rules={{ required: "Matter is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      showSearch
                      placeholder={!selectedClientId ? "Select a client first" : "Select matter"}
                      value={field.value || undefined}
                      onChange={(selectedId) => {
                        const selected = filteredMatters.find((m) => m.matterid === selectedId);
                        field.onChange(selectedId);
                        setValue("mattername", selected?.mattername || "");
                        if (selected?.currency) {
                          setValue("currency", selected.currency);
                        }
                      }}
                      disabled={!selectedClientId}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      options={filteredMatters.map((m) => ({
                        value: m.matterid,
                        label: m.mattername || m.matterid,
                      }))}
                      notFoundContent={
                        !selectedClientId
                          ? "Select a client first"
                          : listsLoading
                            ? <Spin size="small" />
                            : "No matters found"
                      }
                      className={errors.matterid ? "field-error" : ""}
                    />
                  )}
                />
                {errors.matterid && <span className="error">{errors.matterid.message}</span>}
              </div>
            </div>

            {/* ── Row 2: Date of Log + Tax Type ── */}
            <div className="form-row">
              <div className="form-group">
                <Label required>Date of Log</Label>
                <div
                  style={{ position: "relative", cursor: "pointer" }}
                  onClick={(e) => {
                    const input = e.currentTarget.querySelector('input[type="date"]');
                    if (input && typeof input.showPicker === "function") {
                      input.showPicker();
                    }
                  }}
                >
                  <input
                    type="date"
                    style={{ cursor: "pointer", pointerEvents: "none" }}
                    className={errors.dateoflog ? "field-error" : ""}
                    {...register("dateoflog", { required: "Date of log is required" })}
                  />
                </div>
                {errors.dateoflog && <span className="error">{errors.dateoflog.message}</span>}
              </div>

              <div className="form-group">
                <Label>Tax Type</Label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      value="VAT"
                      checked={taxtype === "VAT"}
                      onChange={() => setValue("taxtype", "VAT")}
                    /> VAT
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="Non-VAT"
                      checked={taxtype === "Non-VAT"}
                      onChange={() => setValue("taxtype", "Non-VAT")}
                    /> Non-VAT
                  </label>
                </div>
              </div>
            </div>

            {/* ── Row 3: Type + Total Amount ── */}
            <div className="form-row">
              <div className="form-group">
                <Label>Type</Label>
                <select
                  value={type}
                  onChange={(e) => setValue("type", e.target.value)}
                >
                  <option>Medical</option>
                  <option>Travel</option>
                  <option>Stationery</option>
                  <option>Food</option>
                  <option>Other</option>
                  <option>Subcontract</option>
                </select>
              </div>

              <div className="form-group">
                <Label required>Total Amount</Label>
                <div className={`rate-row${errors.totalamount ? " field-error" : ""}`}>
                  <span className="currency-symbol">
                    <span style={{ fontSize: 14 }}>{currencySymbol}</span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>{selectedCurrency}</span>
                  </span>
                  <input
                    type="number"
                    placeholder="0"
                    {...register("totalamount", {
                      required: "Total amount is required",
                      validate: (v) =>
                        (v !== "" && !isNaN(parseFloat(v)) && parseFloat(v) > 0) ||
                        "Total amount must be greater than 0",
                    })}
                  />
                </div>
                {errors.totalamount && <span className="error">{errors.totalamount.message}</span>}
              </div>
            </div>

            {/* ── Bills / Attachments (multiple) ── */}
            <div className="form-group bills-section">
              <Label>Attachments</Label>

              {existingAttachments.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  {existingAttachments.map((att, index) => (
                    <div className="bill-item" key={`existing-${index}`}>
                      <span className="bill-icon">📄</span>
                      <span
                        className="bill-name"
                        title={att.fileName}
                        style={{ cursor: "pointer", color: "#0d9488" }}
                        onClick={async () => {
                          if (att.url && !att.objectKey) {
                            window.open(att.url, "_blank");
                            return;
                          }
                          if (att.objectKey) {
                            try {
                              const res = await CALL_API(
                                "insyts/timesheet/claims?preview",
                                "post",
                                { objectKey: att.objectKey }
                              );
                              const url = res?.response?.url ?? res?.url;
                              if (url) window.open(url, "_blank");
                              else message.error("Preview URL not available");
                            } catch {
                              message.error("Failed to load file");
                            }
                            return;
                          }
                          message.error("No file available");
                        }}
                      >
                        {att.fileName || att.name}
                      </span>
                      <span className="bill-size">{formatBytes(att.fileSize || 0)}</span>
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() =>
                          setExistingAttachments((prev) => prev.filter((_, i) => i !== index))
                        }
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {bills.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  {bills.map((file, index) => (
                    <div className="bill-item" key={`new-${index}`}>
                      <span className="bill-icon">📄</span>
                      <span className="bill-name" title={file.name}>{file.name}</span>
                      <span className="bill-size">{formatBytes(file.size)}</span>
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => handleRemoveBill(index)}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <input
                type="file"
                id="billsUpload"
                multiple
                style={{ display: "none" }}
                onChange={handleAddBills}
              />

              <button
                type="button"
                className="upload-btn"
                onClick={() => document.getElementById("billsUpload").click()}
              >
                {bills.length > 0 ? "Add More Bills" : "Upload Bill"}
              </button>

              {(bills.length + existingAttachments.length) > 0 && (
                <span className="bills-limit-note">
                  {bills.length + existingAttachments.length} bill{(bills.length + existingAttachments.length) !== 1 ? "s" : ""} attached
                </span>
              )}
            </div>

            {/* ── Description ── */}
            <div className="form-group" style={{ marginBottom: 4 }}>
              <Label>Log Description</Label>
              <DescriptionBlock
                value={description}
                onChange={(v) => setValue("description", v)}
              />
            </div>

            <ModalFooter>
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving…" : isEdit ? "Save Changes" : "Create"}
              </button>
            </ModalFooter>

          </form>
        </ModalBody>
      </Spin>
    </Modal>
  );
};

export default ClaimsLogModal;